import {expect, describe, test, vi, beforeEach} from 'vitest';
import {outputWarn} from '@shopify/cli-kit/node/output';

import {createDeploy} from '../../deploy/index.js';
import {verifyConfig} from '../../utils/utils.js';
import {getBugsnag} from '../../utils/bugsnag.js';

import {Deploy} from './deploy.js';

vi.mock('@shopify/cli-kit/node/output');
vi.mock('../../deploy/index.js');
vi.mock('../../utils/utils.js');
const mockBugsnag = {
  notify: vi.fn(),
  addMetadata: vi.fn(),
};

vi.mock('../../utils/bugsnag.js', () => ({
  getBugsnag: vi.fn().mockReturnValue(undefined),
  initializeBugsnag: vi.fn(),
}));
vi.mock('@shopify/cli-kit/node/path', () => ({
  normalizePath: vi.fn((path: string) => path),
}));
vi.mock('@shopify/cli-kit/node/dot-env', () => ({
  readAndParseDotEnv: vi.fn(),
}));

describe('Deploy command', () => {
  let deploy: Deploy;

  beforeEach(() => {
    deploy = new Deploy([], {
      runHook: vi.fn(),
    } as any);
    vi.clearAllMocks();
    mockBugsnag.notify.mockClear();
    mockBugsnag.addMetadata.mockClear();

    vi.mocked(getBugsnag).mockReturnValue(undefined);
  });

  test('should handle errors and call outputWarn', async () => {
    const errorMessage = 'Test deployment error';
    const error = new Error(errorMessage);

    vi.spyOn(deploy, 'parse' as any).mockResolvedValue({
      flags: {token: 'test-token', path: './'},
    } as any);

    vi.mocked(verifyConfig).mockRejectedValueOnce(error);

    const exitSpy = vi.spyOn(deploy, 'exit').mockImplementation(() => {
      throw new Error('Process exit called');
    });

    await expect(deploy.run()).rejects.toThrow('Process exit called');

    expect(outputWarn).toHaveBeenCalledWith(errorMessage);
    expect(exitSpy).toHaveBeenCalledWith(1);
  });

  test('should handle string errors and call outputWarn', async () => {
    const errorMessage = 'String error message';

    vi.spyOn(deploy, 'parse' as any).mockResolvedValue({
      flags: {token: 'test-token', path: './'},
    } as any);

    vi.mocked(verifyConfig).mockRejectedValueOnce(errorMessage);

    const exitSpy = vi.spyOn(deploy, 'exit').mockImplementation(() => {
      throw new Error('Process exit called');
    });

    await expect(deploy.run()).rejects.toThrow('Process exit called');

    expect(outputWarn).toHaveBeenCalledWith(errorMessage);
    expect(exitSpy).toHaveBeenCalledWith(1);
  });

  test('should succeed when deployment completes without errors', async () => {
    vi.spyOn(deploy, 'parse' as any).mockResolvedValue({
      flags: {token: 'test-token', path: './'},
    } as any);

    vi.mocked(verifyConfig).mockResolvedValueOnce(undefined);
    vi.mocked(createDeploy).mockResolvedValueOnce(undefined);

    await deploy.run();

    expect(outputWarn).not.toHaveBeenCalled();
  });

  test('should notify Observe when enabled and error occurs', async () => {
    const errorMessage = 'Bugsnag test error';
    const error = new Error(errorMessage);

    vi.spyOn(deploy, 'parse' as any).mockResolvedValue({
      flags: {token: 'test-token', path: './', disableBugsnag: false},
    } as any);

    vi.mocked(getBugsnag).mockReturnValue(mockBugsnag as any);
    vi.mocked(verifyConfig).mockRejectedValueOnce(error);

    vi.spyOn(deploy, 'exit').mockImplementation(() => {
      throw new Error('Process exit called');
    });

    await expect(deploy.run()).rejects.toThrow('Process exit called');

    expect(mockBugsnag.notify).toHaveBeenCalledWith(error);
    expect(outputWarn).toHaveBeenCalledWith(errorMessage);
  });

  test('should not notify Observe when disabled', async () => {
    const errorMessage = 'No Bugsnag test error';
    const error = new Error(errorMessage);

    vi.spyOn(deploy, 'parse' as any).mockResolvedValue({
      flags: {token: 'test-token', path: './', disableBugsnag: true},
    } as any);

    vi.mocked(getBugsnag).mockReturnValue(undefined);
    vi.mocked(verifyConfig).mockRejectedValueOnce(error);

    vi.spyOn(deploy, 'exit').mockImplementation(() => {
      throw new Error('Process exit called');
    });

    await expect(deploy.run()).rejects.toThrow('Process exit called');

    expect(mockBugsnag.notify).not.toHaveBeenCalled();
    expect(outputWarn).toHaveBeenCalledWith(errorMessage);
  });
});
